import { useToggle } from '@commenda/kakashi';
import { useWorkflow } from '../../context/WorkflowContext';
import { AutoLayoutPanel } from '../AutoLayoutPanel';
import { ManageGroupsModal } from '../modals/ManageGroupsModal';
import { NodePalette } from '../NodePalette';
import { PropertyPanel } from '../PropertyPanel';
import { WorkflowCanvas } from '../WorkflowCanvas';
import { TRenderCanvasOverlayParams, TRenderToolbarParams } from './types';

/** Owns the viewport unless a host overrides it: every mount but the affiliate builder puts the
 * editor at the top of its page. */
const DEFAULT_ROOT_CLASS_NAME = 'h-screen w-full flex flex-col bg-bg-secondary';

export const WorkflowEditorContent = ({
  renderToolbar,
  renderCanvasOverlay,
  rootClassName = DEFAULT_ROOT_CLASS_NAME,
}: {
  renderToolbar?: (params: TRenderToolbarParams) => React.ReactNode;
  renderCanvasOverlay?: (params: TRenderCanvasOverlayParams) => React.ReactNode;
  rootClassName?: string;
}) => {
  const manageGroupsModalVisibility = useToggle();
  const autoLayoutPanelVisibility = useToggle();
  const {
    selectedEntities,
    setSelectedEntities,
    workflow,
    undo,
    redo,
    setWorkflow,
    hasUnsavedChanges,
    markAsSaved,
    isReadOnly,
  } = useWorkflow();

  const handlers: TRenderToolbarParams['handlers'] = {
    onUndo: () => undo(),
    onRedo: () => redo(),
    onAddGroup: () => manageGroupsModalVisibility.show(),
    onAutoLayout: () => autoLayoutPanelVisibility.toggle(),
  };

  return (
    <div className={rootClassName}>
      {/* Toolbar */}
      {renderToolbar &&
        renderToolbar({
          workflow,
          setWorkflow,
          hasUnsavedChanges,
          markAsSaved,
          handlers,
        })}
      {/* The builder agent sits beside the editor, in WorkflowBuilderAgentLayout, not inside it. */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Center - Canvas, plus the palette and property panel that sit over it */}
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {/* Left Panel - Node Palette. Hidden while read-only: the canvas refuses palette drops, and a
              palette that silently does nothing reads as a bug. */}
          {!isReadOnly && (
            <div className="absolute left-0 top-0 z-10 w-56 border-r border-stroke-3 bg-white overflow-auto rounded-3xl m-4">
              <NodePalette />
            </div>
          )}

          <div className="flex-1 bg-bg-secondary">
            <WorkflowCanvas onNodeDelete={() => setSelectedEntities([])} />
          </div>

          {renderCanvasOverlay?.({})}

          {/* Right Panel - Property Panel. Renders nothing at all when no node is selected: an always-present
              empty wrapper shows as a stray white box beside the chat panel. */}
          {selectedEntities.length === 1 && (
            <div className="border-l border-stroke-3 bg-white overflow-auto rounded-3xl mx-4 mt-4 max-h-[calc(100vh-100px)]">
              <PropertyPanel
                key={selectedEntities[0].id}
                selectedEntity={selectedEntities[0]}
              />
            </div>
          )}
        </div>
      </div>
      <AutoLayoutPanel isVisible={autoLayoutPanelVisibility.visible} />
      <ManageGroupsModal
        isOpen={manageGroupsModalVisibility.visible}
        onClose={() => manageGroupsModalVisibility.hide()}
      />
    </div>
  );
};
